import { lazy } from 'react';
import type { ParkingSectionDef } from './context';

// Pages and tabs load when first opened (NFR-04).
const FacilitiesTab = lazy(() => import('./components/FacilitiesTab').then((m) => ({ default: m.FacilitiesTab })));
const VehiclesTab = lazy(() => import('./components/VehiclesTab').then((m) => ({ default: m.VehiclesTab })));
const VehicleTypesTab = lazy(() => import('./components/VehiclesTab').then((m) => ({ default: m.VehicleTypesTab })));
const TariffsTab = lazy(() => import('./components/TariffsTab').then((m) => ({ default: m.TariffsTab })));
const SpecialDaysTab = lazy(() => import('./components/SetupTabs').then((m) => ({ default: m.SpecialDaysTab })));
const ParkingSettingsTab = lazy(() => import('./components/SetupTabs').then((m) => ({ default: m.ParkingSettingsTab })));
const ConsoleTab = lazy(() => import('./components/OperationsTabs').then((m) => ({ default: m.ConsoleTab })));
const SessionsTab = lazy(() => import('./components/OperationsTabs').then((m) => ({ default: m.SessionsTab })));
const MyShiftTab = lazy(() => import('./components/CashierTabs').then((m) => ({ default: m.MyShiftTab })));
const ShiftsTab = lazy(() => import('./components/CashierTabs').then((m) => ({ default: m.ShiftsTab })));
const PaymentsTab = lazy(() => import('./components/CashierTabs').then((m) => ({ default: m.PaymentsTab })));
const RefundsTab = lazy(() => import('./components/FinanceTabs').then((m) => ({ default: m.RefundsTab })));
const PostingsTab = lazy(() => import('./components/FinanceTabs').then((m) => ({ default: m.PostingsTab })));
const ReconciliationTab = lazy(() => import('./components/FinanceTabs').then((m) => ({ default: m.ReconciliationTab })));
const BankingTab = lazy(() => import('./components/FinanceTabs').then((m) => ({ default: m.BankingTab })));
const SettlementsTab = lazy(() => import('./components/FinanceTabs').then((m) => ({ default: m.SettlementsTab })));
const AccountBillingTab = lazy(() => import('./components/FinanceTabs').then((m) => ({ default: m.AccountBillingTab })));
const PermitsTab = lazy(() => import('./components/CommercialTabs').then((m) => ({ default: m.PermitsTab })));
const PermitTypesTab = lazy(() => import('./components/CommercialTabs').then((m) => ({ default: m.PermitTypesTab })));
const DiscountsTab = lazy(() => import('./components/CommercialTabs').then((m) => ({ default: m.DiscountsTab })));
const ReservationsTab = lazy(() => import('./components/CommercialTabs').then((m) => ({ default: m.ReservationsTab })));
const GatesTab = lazy(() => import('./components/DevicesTabs').then((m) => ({ default: m.GatesTab })));
const CamerasTab = lazy(() => import('./components/DevicesTabs').then((m) => ({ default: m.CamerasTab })));
const ReadsTab = lazy(() => import('./components/DevicesTabs').then((m) => ({ default: m.ReadsTab })));
const AccessLogTab = lazy(() => import('./components/DevicesTabs').then((m) => ({ default: m.AccessLogTab })));
const PrivacyTab = lazy(() => import('./components/PrivacyTab').then((m) => ({ default: m.PrivacyTab })));
const GatewaysTab = lazy(() => import('./components/GatewaysTab').then((m) => ({ default: m.GatewaysTab })));

/**
 * The parking module's sections, one per menu item, each with its leaves. The URL's last segment
 * names the leaf, so every tab is a real address; the rights are read once by the page.
 */
export const parkingSections = {
  operations: {
    area: 'Parking', title: 'Operations', basePath: '/parking/operations',
    description: 'Entries and exits at the gates, the stays inside, and the exceptions waiting for a supervisor.',
    views: [
      { key: 'console', label: 'Entry and exit' },
      { key: 'sessions', label: 'Stays' },
      { key: 'exceptions', label: 'Exceptions' },
    ],
    render: (view, ctx) => (view === 'sessions' ? <SessionsTab ctx={ctx} /> : view === 'exceptions' ? <SessionsTab ctx={ctx} exceptionsOnly /> : <ConsoleTab ctx={ctx} />),
  } satisfies ParkingSectionDef<'console' | 'sessions' | 'exceptions'>,

  cashier: {
    area: 'Parking', title: 'Cashier', basePath: '/parking/cashier',
    description: 'The cashier\'s shift and cash-up, every payment with its receipt, the shifts waiting for review, and refunds to pay.',
    views: [
      { key: 'shift', label: 'My shift' },
      { key: 'payments', label: 'Payments' },
      { key: 'shifts', label: 'Shifts' },
      { key: 'refunds', label: 'Refunds' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'payments': return <PaymentsTab />;
        case 'shifts': return <ShiftsTab ctx={ctx} />;
        case 'refunds': return <RefundsTab ctx={ctx} />;
        default: return <MyShiftTab ctx={ctx} />;
      }
    },
  } satisfies ParkingSectionDef<'shift' | 'payments' | 'shifts' | 'refunds'>,

  commercial: {
    area: 'Parking', title: 'Commercial', basePath: '/parking/commercial',
    description: 'Permits and subscriptions, discount and validation codes, and reservations.',
    views: [
      { key: 'permits', label: 'Permits' },
      { key: 'permit-types', label: 'Permit types' },
      { key: 'discounts', label: 'Discounts and validations' },
      { key: 'reservations', label: 'Reservations' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'permit-types': return <PermitTypesTab ctx={ctx} />;
        case 'discounts': return <DiscountsTab ctx={ctx} />;
        case 'reservations': return <ReservationsTab ctx={ctx} />;
        default: return <PermitsTab ctx={ctx} />;
      }
    },
  } satisfies ParkingSectionDef<'permits' | 'permit-types' | 'discounts' | 'reservations'>,

  devices: {
    area: 'Parking', title: 'Devices', basePath: '/parking/devices',
    description: 'Barriers and the cameras that read plates: reads waiting for an operator, what the cameras read, and every decision at a gate.',
    views: [
      { key: 'gates', label: 'Gates' },
      { key: 'review', label: 'Waiting for review' },
      { key: 'reads', label: 'Plate reads' },
      { key: 'access-log', label: 'Access log' },
      { key: 'cameras', label: 'Cameras' },
      { key: 'gateways', label: 'Site gateways' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'review': return <ReadsTab ctx={ctx} reviewOnly />;
        case 'reads': return <ReadsTab ctx={ctx} />;
        case 'access-log': return <AccessLogTab />;
        case 'cameras': return <CamerasTab ctx={ctx} />;
        case 'gateways': return <GatewaysTab ctx={ctx} />;
        default: return <GatesTab ctx={ctx} />;
      }
    },
  } satisfies ParkingSectionDef<'gates' | 'review' | 'reads' | 'access-log' | 'cameras' | 'gateways'>,

  finance: {
    area: 'Parking', title: 'Finance', basePath: '/parking/finance',
    description: 'Parking and the ledger: what was posted and what was refused, cash banked, provider settlements, account billing, and the reconciliation.',
    views: [
      { key: 'postings', label: 'Posting monitor' },
      { key: 'reconciliation', label: 'Reconciliation' },
      { key: 'banking', label: 'Banking' },
      { key: 'settlements', label: 'Settlements' },
      { key: 'billing', label: 'Account billing' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'reconciliation': return <ReconciliationTab />;
        case 'banking': return <BankingTab ctx={ctx} />;
        case 'settlements': return <SettlementsTab ctx={ctx} />;
        case 'billing': return <AccountBillingTab ctx={ctx} />;
        default: return <PostingsTab ctx={ctx} />;
      }
    },
  } satisfies ParkingSectionDef<'postings' | 'reconciliation' | 'banking' | 'settlements' | 'billing'>,

  vehicles: {
    area: 'Parking', title: 'Vehicles', basePath: '/parking/vehicles',
    description: 'The vehicle register: regulars, account customers\' and staff vehicles, and the blacklist. Visitors need no record.',
    views: [
      { key: 'register', label: 'Register' },
      { key: 'blacklist', label: 'Blacklist' },
      { key: 'types', label: 'Vehicle types' },
    ],
    render: (view, ctx) => (view === 'blacklist' ? <VehiclesTab ctx={ctx} blacklistOnly /> : view === 'types' ? <VehicleTypesTab ctx={ctx} /> : <VehiclesTab ctx={ctx} />),
  } satisfies ParkingSectionDef<'register' | 'blacklist' | 'types'>,

  settings: {
    area: 'Parking', title: 'Parking Settings', basePath: '/parking/settings',
    description: 'The car parks, their tariffs and special days, and how parking prices, taxes and posts.',
    views: [
      { key: 'facilities', label: 'Facilities' },
      { key: 'tariffs', label: 'Tariffs' },
      { key: 'special-days', label: 'Special days' },
      { key: 'general', label: 'Settings and accounts' },
      { key: 'privacy', label: 'Devices and privacy' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'tariffs': return <TariffsTab ctx={ctx} />;
        case 'special-days': return <SpecialDaysTab ctx={ctx} />;
        case 'general': return <ParkingSettingsTab ctx={ctx} />;
        case 'privacy': return <PrivacyTab ctx={ctx} />;
        default: return <FacilitiesTab ctx={ctx} />;
      }
    },
  } satisfies ParkingSectionDef<'facilities' | 'tariffs' | 'special-days' | 'general' | 'privacy'>,
};
